// External libraries
import axios from "axios";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ulid } from "ulid";
import { v4 as uuidv4 } from "uuid";

// FontAwesome icons
import {
  faHome,
  faStar as faStarRegular,
  faStar as faStarSolid,
} from "@fortawesome/pro-duotone-svg-icons";

// Preact Signals
import { batch } from "@preact/signals-react";

// Kendo React components
import { Chip } from "@progress/kendo-react-buttons";
import { clone, cloneArray } from "@progress/kendo-react-common";
import { Pager } from "@progress/kendo-react-data-tools";
import { Loader } from "@progress/kendo-react-indicators";
import { TextBox } from "@progress/kendo-react-inputs";
import { Breadcrumb, TileLayout } from "@progress/kendo-react-layout";
import { ListView } from "@progress/kendo-react-listview";

// Internal components
import CloneButton from "../Buttons/CloneButton";
import CreateButton from "../Buttons/CreateButton";
import DeleteButton from "../Buttons/DeleteButton";
import EditButton from "../Buttons/EditButton";
import HidePermissionsButton from "../Buttons/HidePermissionsButton";
import ShowPermissionsButton from "../Buttons/ShowPermissionsButton";
import { ColorChangingIcon } from "../Common/UI/ColorChangingIcon";
import ConfirmDeleteDialog from "../Notification/ConfirmDeleteDialog";

// Utilities
import { userHasAccessToResource } from "../Utilities/AuthorizationUtil";
import { handleSignalRTrigger } from "../Utilities/CommonUtils";

// Signals
import {
  galleryConstructorGridRows,
  galleryConstructorVisibility,
} from "../Common/GalleryConstructor/GalleryConstructorSignals";
import {
  allTemplates,
  isAdmin,
  isTemplateFavorite,
  notification,
  toggleFavoriteTemplate,
  userData,
} from "../Common/Store/CommonSignals";
import { selectedHamburgerIndex } from "../Navigation/HamburgerMenu/HamburgerMenuSignals";
import { createEmptyTemplate, showAutomationsWindow, templateData } from "./NewTemplateSignals";
import { filteredTemplates, pageState, search, skip, take } from "./ViewTemplateSignals";

const CreateTemplateButton = ({ navigate }) => {
  return (
    <CreateButton
      component="Create Template"
      onClick={() => {
        galleryConstructorVisibility.value = false;
        showAutomationsWindow.value = false;
        galleryConstructorGridRows.value = [];
        templateData.value = { ...createEmptyTemplate(), owner: userData.value.userEmail };
        navigate("/templates/create");
      }}
    />
  );
};

const RenderFields = ({ fields }) => {
  if (!fields) {
    return null;
  }

  return (
    <>
      {fields.map((field) => (
        <div
          className="k-pl-4"
          key={uuidv4()}
        >
          {field.fieldName} ({field.fieldType})
        </div>
      ))}
    </>
  );
};

const SectionItemRender = ({ dataItem }) => (
  <div>
    <div className="k-mt-1 k-pl-2">
      <strong>Section Name:</strong> {dataItem.sectionName}
    </div>
    <RenderFields fields={dataItem.fields} />
  </div>
);

const TemplateRenderer = ({ template }) => {
  if (!template?.sections) {
    return <div>No data available.</div>;
  }

  return (
    <ListView
      className="k-var--neutral-30 k-rounded-lg k-overflow-y-auto k-flex-grow"
      data={template.sections}
      item={SectionItemRender}
    />
  );
};

function ViewTemplates() {
  selectedHamburgerIndex.value = 3;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState();
  const [deleting, setDeleting] = useState(null);
  const [viewPerms, setViewPerms] = useState({});
  const navigate = useNavigate();
  const userEmail = userData.value?.userEmail;
  const userIsAdmin = isAdmin.value;

  const HandleDeleteTemplate = async (id) => {
    setSelectedTemplateId(id);
    setDeleting(id);
    setShowDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setShowDeleteDialog(false);
    setDeleting(null);
  };

  async function handleDeleteDialogConfirm() {
    setShowDeleteDialog(false);
    setDeleting(null);

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_ENDPOINT}/Template/${selectedTemplateId}?userId=${userEmail}`
      );
      notification.value = {
        show: true,
        type: "success",
        message: "Template deleted successfully!",
      };
      allTemplates.value = allTemplates.value.filter(
        (template) => template.id !== selectedTemplateId
      );
      await handleSignalRTrigger("DELETE", "Template", { id: selectedTemplateId });
    } catch (error) {
      notification.value = {
        show: true,
        type: "error",
        message: "Failed to delete template!",
        error: error,
      };
      throw error;
    }
  }

  const items = filteredTemplates.value
    .filter((template) => {
      if (userIsAdmin) {
        return true;
      }
      return userHasAccessToResource(template, userEmail);
    })

    .map((template) => ({
      key: template.id,
      resizable: false,
      reordorable: false,
      style: { cursor: "default" },
      header: (
        <div className="k-row">
          <div className="dataset-tile-header">
            <div className="k-d-flex k-align-items-center k-gap-2">
              <strong name={"lblTemplateHeaderName_" + template.templateName}>
                {template.templateName}
              </strong>
            </div>
            <div>
              {deleting === template.id ? (
                <Loader
                  type="pulsing"
                  themeColor="primary"
                />
              ) : (
                <div className="align-columns">
                  <ColorChangingIcon
                    key={`star-${template.id}-${isTemplateFavorite(template.id)}`}
                    icon={isTemplateFavorite(template.id) ? faStarSolid : faStarRegular}
                    initialPrimaryColor={isTemplateFavorite(template.id) ? "#FFA500" : "#999999"}
                    initialSecondaryColor={
                      isTemplateFavorite(template.id) ? "#FFD700" : "#979797ff"
                    }
                    initialSize={18}
                    hoverSize={18}
                    tooltip={
                      isTemplateFavorite(template.id) ? "Remove from favorites" : "Add to favorites"
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavoriteTemplate(template.id);
                    }}
                  />
                  <EditButton
                    component="Edit Template"
                    name={"btnTemplateEdit_" + template.templateName}
                    onClick={() => {
                      batch(() => {
                        galleryConstructorVisibility.value = false;
                        galleryConstructorGridRows.value = [];
                        showAutomationsWindow.value = false;
                        //Update the columns of the template data connections
                        const dataConnections = cloneArray(
                          template?.properties?.DataConnections || []
                        );
                        const updatedTemplate = {
                          ...template,
                          properties: {
                            ...template.properties,
                            DataConnections: dataConnections?.map((dataConnection) => {
                              let columns = {};
                              if (dataConnection.type === "Template") {
                                const selectedTemplate = allTemplates.value?.find((template) => {
                                  return template.id === dataConnection.id;
                                });
                                columns["id"] = { selected: true };
                                columns["createdDate"] = { selected: true };
                                columns["_ts"] = { selected: true };
                                columns["entryAuthor"] = { selected: true };
                                selectedTemplate?.sections?.forEach((section) => {
                                  section.fields?.forEach((field) => {
                                    columns[field.fieldName] = { selected: true };
                                  });
                                });

                                if (dataConnection.columns) {
                                  Object.entries(dataConnection.columns).forEach(([key, value]) => {
                                    if (Object.hasOwn(columns, key)) {
                                      columns[key] = { ...columns[key], ...value };
                                    }
                                  });
                                }
                              } else {
                                columns = dataConnection.columns || {};
                              }

                              return {
                                ...dataConnection,
                                columns: clone(columns),
                              };
                            }),
                          },
                        };

                        templateData.value = updatedTemplate;
                      });
                      navigate(`/templates/edit?templateId=${template.id}`);
                    }}
                    data={template}
                  />
                  <CloneButton
                    component="Clone Template"
                    name={"btnTemplateClone_" + template.templateName}
                    onClick={() => {
                      const templateClone = { ...template };
                      templateClone.templateName += " - Clone";
                      templateClone.id = ulid();

                      batch(() => {
                        templateData.value = templateClone;
                        galleryConstructorVisibility.value = false;
                        galleryConstructorGridRows.value = [];
                      });

                      navigate(`/templates/create`);
                    }}
                    data={template}
                  />
                  <DeleteButton
                    component="Delete Template"
                    name={"btnTemplateDelete_" + template.templateName}
                    onClick={() => HandleDeleteTemplate(template.id)}
                    data={template}
                  />
                </div>
              )}
            </div>
          </div>
          <div
            className="dataset-tile-subheader"
            name={"lblTemplateID_" + template.templateName}
          >
            {template.id}
          </div>
        </div>
      ),
      body: (
        <div className="card-body">
          <div className="card-info">
            <div>
              <strong>Company:</strong> {template.company}{" "}
            </div>
            <div>
              <strong>Branch:</strong> {template.branch}{" "}
            </div>
            <div>
              <strong>Area:</strong> {template.area}{" "}
            </div>
            <div>
              <strong>Department:</strong> {template.department}{" "}
            </div>
            <div>
              <strong>Owner:</strong> {template.owner}
            </div>
            {(template.properties?.Tags || []).length > 0 && (
              <div
                className="k-mt-2 k-d-flex k-flex-wrap"
                style={{ gap: "4px" }}
              >
                {(template.properties?.Tags || []).map((tag, index) => (
                  <Chip
                    key={tag + "_" + index}
                    text={tag}
                    size="small"
                  />
                ))}
              </div>
            )}
          </div>
          <div className="permissions-btn">
            <strong>Permissions:</strong>
            {!viewPerms[template.id] && (
              <ShowPermissionsButton
                onClick={() =>
                  setViewPerms((prevPerms) => ({
                    ...prevPerms,
                    [template.id]: true,
                  }))
                }
              />
            )}
            {viewPerms[template.id] && (
              <HidePermissionsButton
                onClick={() =>
                  setViewPerms((prevPerms) => ({
                    ...prevPerms,
                    [template.id]: false,
                  }))
                }
              />
            )}
          </div>
          {viewPerms[template.id] && (
            <div className="k-var--neutral-30 k-rounded-lg k-overflow-y-auto k-flex-grow">
              {template.coOwners.length > 0 && (
                <div>
                  <strong className="k-ml-5">Co-Owners</strong>{" "}
                  {template.coOwners.map((coOwner) => (
                    <div
                      key={coOwner.user}
                      className="k-ml-10"
                    >
                      {coOwner.user}
                    </div>
                  ))}
                </div>
              )}
              {template.sharedPermission.filter((sp) => sp.permission === "View").length > 0 && (
                <div>
                  <strong className="k-ml-5">View Permissions: </strong>
                  {template.sharedPermission
                    .filter((sp) => sp.permission === "View")
                    .map((sharedPermission) => (
                      <div
                        key={sharedPermission.entity}
                        className="k-ml-10"
                      >
                        {sharedPermission.entity}
                      </div>
                    ))}
                </div>
              )}
              {template.sharedPermission.filter((sp) => sp.permission === "Modify").length > 0 && (
                <div>
                  <strong className="k-ml-5">Modify Permissions: </strong>
                  {template.sharedPermission
                    .filter((sp) => sp.permission === "Modify")
                    .map((sharedPermission) => (
                      <div
                        key={sharedPermission.entity}
                        className="k-ml-10"
                      >
                        {sharedPermission.entity}
                      </div>
                    ))}
                </div>
              )}
              {template.sharedPermission.filter((sp) => sp.permission === "MakeEntry").length >
                0 && (
                <div>
                  <strong className="k-ml-5">Make Entry Permissions: </strong>
                  {template.sharedPermission
                    .filter((sp) => sp.permission === "MakeEntry")
                    .map((sharedPermission) => (
                      <div
                        key={sharedPermission.entity}
                        className="k-ml-10"
                      >
                        {sharedPermission.entity}
                      </div>
                    ))}
                </div>
              )}
              {template.sharedPermission.filter((sp) => sp.permission === "ViewAuthorOnly").length >
                0 && (
                <div>
                  <strong className="k-ml-5">Make Entry (Restricted View) Permissions: </strong>
                  {template.sharedPermission
                    .filter((sp) => sp.permission === "ViewAuthorOnly")
                    .map((sharedPermission) => (
                      <div
                        key={sharedPermission.entity}
                        className="k-ml-10"
                      >
                        {sharedPermission.entity}
                      </div>
                    ))}
                </div>
              )}
              {template.sharedPermission.filter((sp) => sp.permission === "MakeEntry (Modify All)")
                .length > 0 && (
                <div>
                  <strong className="k-ml-5">Make Entry (Modify All) Permissions: </strong>
                  {template.sharedPermission
                    .filter((sp) => sp.permission === "MakeEntry (Modify All)")
                    .map((sharedPermission) => (
                      <div
                        key={sharedPermission.entity}
                        className="k-ml-10"
                      >
                        {sharedPermission.entity}
                      </div>
                    ))}
                </div>
              )}
              {template.sharedPermission.length === 0 && template.coOwners.length === 0 && (
                <div>
                  <strong className="k-ml-5">Template not shared with anyone.</strong>
                </div>
              )}
            </div>
          )}
          {!viewPerms[template.id] && (
            <div className="card-data-container">
              <strong className="k-ml-2">Template Structure:</strong>
              <div
                key={`${template.id}`}
                className="card-data-container"
              >
                <TemplateRenderer template={template} />
              </div>
            </div>
          )}
        </div>
      ),
    }));

  // Paginate items for TileLayout
  const paginatedItems = items.slice(skip.value, skip.value + take.value);

  // Render components
  return (
    <div>
      <Breadcrumb
        style={{ backgroundColor: "transparent" }}
        data={[
          {
            id: "home",
            icon: (
              <ColorChangingIcon
                icon={faHome}
                initialPrimaryColor="#0078D4"
                initialSecondaryColor="#00aeef"
                initialSize={16}
                hoverSize={16}
              />
            ),
          },
          { id: "viewTemplates_Edge", text: "Templates" },
        ]}
        onItemSelect={(e) => {
          if (e.id === "home") {
            navigate("/");
          }
        }}
      />
      <div>
        <TextBox
          name="txtTemplateSearchBar"
          prefix={() => CreateTemplateButton({ navigate })}
          className="tilelayout-searchbar"
          placeholder="Search by any field..."
          onChange={(e) => {
            search.value = e.target.value;
          }}
          value={search.value}
        />
        <TileLayout
          className="tile-layout"
          items={paginatedItems}
          columns={4}
          rows={1}
          autoFlow="row dense"
          ignoreDrag={(e) => {
            return e.target;
          }}
        />
        <Pager
          className="view-pager"
          skip={pageState.value.skip}
          take={pageState.value.take}
          total={items.length}
          buttonCount={pageState.value.buttonCount}
          info={pageState.value.info}
          type={pageState.value.type}
          previousNext={pageState.value.previousNext}
          onPageChange={(e) =>
            batch(() => {
              skip.value = e.skip;
              take.value = e.take;
            })
          }
        />
      </div>
      {showDeleteDialog && (
        <ConfirmDeleteDialog
          content={"template"}
          handleCancelDelete={handleDeleteDialogClose}
          handleConfirmDelete={handleDeleteDialogConfirm}
        />
      )}
    </div>
  );
}

export default ViewTemplates;
